import { z } from 'zod';
import type { FormDefinition, FormField, ValidationRule } from '../../../types/form-schema.types';

export function generateZodSchema(formDefinition: FormDefinition): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const section of formDefinition.sections) {
    for (const field of section.fields) {
      shape[field.processVariable] = createFieldSchema(field);
    }
  }

  return z.object(shape);
}

function createFieldSchema(field: FormField): z.ZodTypeAny {
  let schema: z.ZodTypeAny;

  switch (field.type) {
    case 'TEXT':
    case 'TEXTAREA':
    case 'RICH_TEXT':
    case 'PHONE':
    case 'IBAN':
    case 'ADDRESS':
      schema = z.string();
      break;

    case 'EMAIL':
      schema = z.string().email('Ungueltige E-Mail-Adresse');
      break;

    case 'NUMBER':
      schema = z.coerce.number();
      break;

    case 'DATE':
    case 'DATETIME':
      schema = z.string();
      break;

    case 'CHECKBOX':
      schema = z.boolean();
      break;

    case 'SELECT':
    case 'RADIO':
      schema = z.string();
      break;

    case 'MULTISELECT':
      schema = z.array(z.string());
      break;

    case 'FILE':
      schema = z.any();
      break;

    case 'SIGNATURE':
      schema = z.string();
      break;

    default:
      schema = z.string();
  }

  for (const rule of field.validationRules) {
    schema = applyValidationRule(schema, rule, field.type);
  }

  if (field.required) {
    if (field.type === 'CHECKBOX') {
      schema = (schema as z.ZodBoolean).refine(
        (val) => val === true,
        { message: 'Dieses Feld ist erforderlich' }
      );
    } else if (field.type === 'MULTISELECT') {
      schema = (schema as z.ZodArray<z.ZodString>).min(1, 'Mindestens eine Auswahl erforderlich');
    } else {
      schema = (schema as z.ZodString).min(1, 'Dieses Feld ist erforderlich');
    }
  } else {
    schema = schema.optional();
  }

  return schema;
}

function applyValidationRule(
  schema: z.ZodTypeAny,
  rule: ValidationRule,
  _fieldType: string
): z.ZodTypeAny {
  const message = rule.messages['de'] || getDefaultMessage(rule);

  switch (rule.type) {
    case 'MIN_LENGTH':
      if (schema instanceof z.ZodString) {
        return schema.min(parseInt(rule.value || '0'), message);
      }
      break;

    case 'MAX_LENGTH':
      if (schema instanceof z.ZodString) {
        return schema.max(parseInt(rule.value || '0'), message);
      }
      break;

    case 'MIN':
      if (schema instanceof z.ZodNumber) {
        return schema.min(parseFloat(rule.value || '0'), message);
      }
      break;

    case 'MAX':
      if (schema instanceof z.ZodNumber) {
        return schema.max(parseFloat(rule.value || '0'), message);
      }
      break;

    case 'PATTERN':
      if (schema instanceof z.ZodString && rule.value) {
        return schema.regex(new RegExp(rule.value), message);
      }
      break;

    case 'EMAIL':
      if (schema instanceof z.ZodString) {
        return schema.email(message);
      }
      break;

    case 'PHONE':
      if (schema instanceof z.ZodString) {
        return schema.regex(/^[+]?[0-9\s-]{6,20}$/, message);
      }
      break;

    case 'IBAN':
      if (schema instanceof z.ZodString) {
        return schema.regex(/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/, message);
      }
      break;
  }

  return schema;
}

function getDefaultMessage(rule: ValidationRule): string {
  switch (rule.type) {
    case 'MIN_LENGTH':
      return `Mindestens ${rule.value} Zeichen erforderlich`;
    case 'MAX_LENGTH':
      return `Maximal ${rule.value} Zeichen erlaubt`;
    case 'MIN':
      return `Wert muss mindestens ${rule.value} sein`;
    case 'MAX':
      return `Wert darf maximal ${rule.value} sein`;
    case 'PATTERN':
      return 'Format ist ungueltig';
    case 'EMAIL':
      return 'Ungueltige E-Mail-Adresse';
    case 'PHONE':
      return 'Ungueltige Telefonnummer';
    case 'IBAN':
      return 'Ungueltige IBAN';
    default:
      return 'Ungueltige Eingabe';
  }
}
